import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { METIERS, L, fmtEUR } from "@/constants/labels";
import { Star, Clock, CheckCircle2, Building2 } from "lucide-react";
import { AuthImage } from "@/components/AuthImage";

const DispoBadge = ({ dispo }) => {
  if (!dispo) return null;
  if (dispo.status === "disponible") return <span className="badge-valid text-[10px]" data-testid="dispo-badge-full"><CheckCircle2 size={10} /> Disponible sur toute la période</span>;
  if (dispo.status === "partiel") {
    const d = dispo.first_available;
    return <span className="badge-warn text-[10px]" data-testid="dispo-badge-partial"><Clock size={10} /> Disponible à partir du {d ? new Date(d).toLocaleDateString("fr-FR") : "?"}</span>;
  }
  return null;
};

export default function SearchPrestataires() {
  const [q, setQ] = useState("");
  const [metier, setMetier] = useState("");
  const [specialite, setSpecialite] = useState("");
  const [sousSpec, setSousSpec] = useState("");
  const [codePostal, setCodePostal] = useState("");
  const [rayonKm, setRayonKm] = useState("");
  const [equipmentCatalog, setEquipmentCatalog] = useState([]);
  const [vehiclesCatalog, setVehiclesCatalog] = useState([]);
  const [equipmentPicks, setEquipmentPicks] = useState([]); // codes cochés
  const [vehiclesPicks, setVehiclesPicks] = useState([]);
  const [permisRemorque, setPermisRemorque] = useState(false);
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [rows, setRows] = useState([]); const [groups, setGroups] = useState(null); const [loading, setLoading] = useState(false);

  const specs = metier && METIERS[metier] ? Object.keys(METIERS[metier]) : [];
  const sspecs = metier && specialite && METIERS[metier]?.[specialite] ? METIERS[metier][specialite] : [];

  useEffect(() => {
    (async () => {
      try {
        const [eq, veh] = await Promise.all([api.get("/equipment/catalog"), api.get("/vehicles/catalog")]);
        setEquipmentCatalog(eq.data || []); setVehiclesCatalog(veh.data || []);
      } catch { /* silencieux, filtres avancés cachés si catalogue indispo */ }
    })();
  }, []);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/prestataires", {
        params: {
          q: q || undefined, metier: metier || undefined,
          specialite: specialite || undefined, sous_specialite: sousSpec || undefined,
          code_postal: codePostal || undefined, rayon_km: rayonKm || undefined,
          equipment_codes: equipmentPicks.length ? equipmentPicks.join(",") : undefined,
          vehicules: vehiclesPicks.length ? vehiclesPicks.join(",") : undefined,
          permis_remorque: permisRemorque || undefined,
          date_debut: dateDebut || undefined,
          date_fin: dateFin || undefined,
        },
      });
      // Réponse : liste plate sans période, {groups, period, count} avec période
      if (data && data.groups) {
        setGroups(data.groups);
        setRows([]);
      } else {
        setGroups(null);
        setRows(data);
      }
    } finally { setLoading(false); }
  }, [q, metier, specialite, sousSpec, codePostal, rayonKm, equipmentPicks, vehiclesPicks, permisRemorque, dateDebut, dateFin]);
  useEffect(() => { search(); }, [search]);

  const toggle = (arr, setter, code) => setter(arr.includes(code) ? arr.filter(c => c !== code) : [...arr, code]);

  return (
    <div>
      <h1 className="text-2xl md:text-3xl font-semibold text-[#1F2933]">{L.wording.rechercheH1}</h1>
      <div className="card mt-4 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="label">Recherche libre</label>
            <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, mot-clé ou SIRET" data-testid="search-q" />
          </div>
          <div>
            <label className="label">Corps de métier</label>
            <select className="select" value={metier} onChange={(e) => { setMetier(e.target.value); setSpecialite(""); setSousSpec(""); }} data-testid="search-metier">
              <option value="">Tous</option>
              {Object.keys(METIERS).map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Spécialité</label>
            <select className="select" value={specialite} onChange={(e) => { setSpecialite(e.target.value); setSousSpec(""); }} disabled={!metier} data-testid="search-specialite">
              <option value="">Toutes</option>
              {specs.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Sous-spécialité</label>
            <select className="select" value={sousSpec} onChange={(e) => setSousSpec(e.target.value)} disabled={!specialite} data-testid="search-sous-specialite">
              <option value="">Toutes</option>
              {sspecs.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Code postal</label>
            <input className="input" value={codePostal} onChange={(e) => setCodePostal(e.target.value)} pattern="\d{5}" data-testid="search-cp" />
          </div>
          <div>
            <label className="label">{L.wording.rechercheRayonHint}</label>
            <input type="number" min="1" max="500" className="input" value={rayonKm} onChange={(e) => setRayonKm(e.target.value)} data-testid="search-rayon" placeholder="Ex. 30" />
          </div>
          <div>
            <label className="label">Date de début du chantier</label>
            <input type="date" className="input" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} data-testid="search-date-debut" />
          </div>
          <div>
            <label className="label">Date de fin</label>
            <input type="date" className="input" value={dateFin} onChange={(e) => setDateFin(e.target.value)} data-testid="search-date-fin" />
          </div>
          <div className="md:col-span-2 flex items-end gap-3">
            <button onClick={search} className="btn-primary w-full md:w-auto" data-testid="search-submit">{L.common.search}</button>
            <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="btn-outline text-xs" data-testid="search-toggle-advanced">
              {showAdvanced ? "Masquer les filtres matériel/véhicules" : "Filtres matériel et véhicules"}
            </button>
          </div>
        </div>

        {showAdvanced && (
          <div className="mt-4 pt-4 border-t border-[#E4E7EB] space-y-4" data-testid="search-advanced">
            <p className="text-xs text-[#3E4C59] italic">
              Ces filtres remontent les prestataires qui correspondent, sans jamais exclure ceux qui n'ont pas renseigné ces informations.
            </p>
            <div>
              <div className="text-xs uppercase tracking-wider text-[#3E4C59] font-semibold mb-2">Matériel et outillage</div>
              <div className="space-y-2">
                {equipmentCatalog.map(cat => (
                  <div key={cat.categorie}>
                    <div className="text-xs text-[#3E4C59] mb-1">{cat.categorie}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {cat.items.map(it => (
                        <button type="button" key={it.code}
                          onClick={() => toggle(equipmentPicks, setEquipmentPicks, it.code)}
                          data-testid={`filter-equipment-${it.code}`}
                          className={`px-2.5 py-1 border rounded-[4px] text-xs ${
                            equipmentPicks.includes(it.code)
                              ? "bg-[#1B3A5C] text-white border-[#1B3A5C]"
                              : "bg-white border-[#CBD5E1] text-[#1F2933]"}`}>
                          {it.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-[#3E4C59] font-semibold mb-2">Véhicules</div>
              <div className="flex flex-wrap gap-1.5">
                {vehiclesCatalog.map(v => (
                  <button type="button" key={v.code}
                    onClick={() => toggle(vehiclesPicks, setVehiclesPicks, v.code)}
                    data-testid={`filter-vehicle-${v.code}`}
                    className={`px-2.5 py-1 border rounded-[4px] text-xs ${
                      vehiclesPicks.includes(v.code)
                        ? "bg-[#1B3A5C] text-white border-[#1B3A5C]"
                        : "bg-white border-[#CBD5E1] text-[#1F2933]"}`}>
                    {v.label}
                  </button>
                ))}
              </div>
              <label className="inline-flex items-center gap-2 text-sm mt-3">
                <input type="checkbox" checked={permisRemorque} onChange={(e) => setPermisRemorque(e.target.checked)} data-testid="filter-permis-remorque" />
                Permis remorque / BE requis
              </label>
            </div>
          </div>
        )}
      </div>

      {groups ? (
        <div className="mt-4 space-y-6">
          <ResultTable
            title={`Disponible sur toute la période (${groups.fully_available.length})`}
            rows={groups.fully_available} loading={loading} showDispo
            testid="group-fully"
          />
          <ResultTable
            title={`Disponible partiellement (${groups.partially_available.length}) — avec date de début réelle`}
            rows={groups.partially_available} loading={false} showDispo
            testid="group-partial"
            emptyMsg="Personne en dispo partielle sur cette période."
          />
        </div>
      ) : (
        <ResultTable title="" rows={rows} loading={loading} testid="results-flat" showDispo={false} />
      )}
    </div>
  );
}

function ResultTable({ title, rows, loading, showDispo, testid, emptyMsg }) {
  return (
    <div data-testid={testid}>
      {title && <h2 className="text-lg font-semibold text-[#1F2933] mb-2">{title}</h2>}
      <div className="card overflow-x-auto">
        <table className="table-compact w-full">
          <thead>
            <tr>
              <th>Raison sociale</th><th>Métier</th><th>Localisation</th>
              <th>Tarif</th><th>Note</th>
              {showDispo && <th>Dispo</th>}<th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={showDispo ? 7 : 6} className="text-center text-[#3E4C59]">{L.common.loading}</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={showDispo ? 7 : 6} className="text-center text-[#3E4C59]">{emptyMsg || L.common.none}</td></tr>}
            {rows.map(r => (
              <tr key={r.user_id} data-testid={`presta-row-${r.user_id}`}>
                <td className="font-semibold">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-[4px] border border-[#E4E7EB] bg-white overflow-hidden shrink-0 flex items-center justify-center" data-testid={`presta-logo-${r.user_id}`}>
                      {r.logo_url
                        ? <AuthImage url={r.logo_url} alt="" className="w-full h-full object-cover" />
                        : <Building2 size={18} className="text-[#CBD5E1]" />}
                    </div>
                    <span>{r.raison_sociale}</span>
                  </div>
                </td>
                <td>{[r.metier, r.specialite].filter(Boolean).join(" • ") || "—"}</td>
                <td>
                  {r.ville ? `${r.ville} (${r.code_postal || "?"})` : "—"}
                  {r.rayon_km ? ` · rayon ${r.rayon_km} km` : ""}
                  {r.distance_km != null && <div className="text-xs text-[#3E4C59]">Distance : {r.distance_km} km</div>}
                </td>
                <td>
                  {r.tarif_mensuel_ht ? <div>{fmtEUR(r.tarif_mensuel_ht)}<span className="text-[#3E4C59]">/mois</span></div> : null}
                  {r.tarif_horaire ? <div>{fmtEUR(r.tarif_horaire)}<span className="text-[#3E4C59]">/h</span></div> : null}
                  {r.tarif_journalier ? <div>{fmtEUR(r.tarif_journalier)}<span className="text-[#3E4C59]">/j</span></div> : null}
                  {!r.tarif_mensuel_ht && !r.tarif_horaire && !r.tarif_journalier && "—"}
                </td>
                <td>{r.note_moyenne ? <span className="inline-flex items-center gap-1"><Star size={14} className="text-[#E8710A]" /> {r.note_moyenne} <span className="text-[#3E4C59]">({r.nb_avis})</span></span> : "—"}</td>
                {showDispo && <td><DispoBadge dispo={r.dispo} /></td>}
                <td><Link to={`/app/prestataires/${r.user_id}`} className="btn-outline" data-testid={`presta-consulter-${r.user_id}`}>Consulter</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
